import i18n, { SUPPORTED_LANGUAGES } from '../i18n';

/**
 * Feste Halsbandfarben-Auswahl - der gespeicherte Wert ist einer dieser Schluessel, uebersetzt via breeding:plan.collarColors.<key>.
 * Bunte Farben gibt es zusaetzlich hell und dunkel, weil ein Wurf oft z.B. Hell- und Dunkelblau traegt.
 */
export const COLLAR_COLOR_KEYS = [
  'lightRed',
  'red',
  'darkRed',
  'lightBlue',
  'blue',
  'darkBlue',
  'lightGreen',
  'green',
  'darkGreen',
  'lightYellow',
  'yellow',
  'darkYellow',
  'lightOrange',
  'orange',
  'darkOrange',
  'lightViolet',
  'violet',
  'darkViolet',
  'lightPink',
  'pink',
  'darkPink',
  'lightTurquoise',
  'turquoise',
  'darkTurquoise',
  'lightBrown',
  'brown',
  'darkBrown',
  'black',
  'white',
  'lightGray',
  'gray',
  'darkGray'
] as const;

export type CollarColorKey = (typeof COLLAR_COLOR_KEYS)[number];

export const COLLAR_COLOR_SWATCHES: Record<CollarColorKey, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  orange: '#f97316',
  violet: '#a855f7',
  pink: '#ec4899',
  turquoise: '#2dd4bf',
  brown: '#92400e',
  black: '#1f2937',
  white: '#f9fafb',
  gray: '#9ca3af',
  lightRed: '#fca5a5',
  darkRed: '#991b1b',
  lightBlue: '#93c5fd',
  darkBlue: '#1e40af',
  lightGreen: '#86efac',
  darkGreen: '#166534',
  lightYellow: '#fde047',
  darkYellow: '#a16207',
  lightOrange: '#fdba74',
  darkOrange: '#9a3412',
  lightViolet: '#d8b4fe',
  darkViolet: '#6b21a8',
  lightPink: '#f9a8d4',
  darkPink: '#9d174d',
  lightTurquoise: '#99f6e4',
  darkTurquoise: '#0f766e',
  lightBrown: '#c08457',
  darkBrown: '#451a03',
  lightGray: '#d1d5db',
  darkGray: '#4b5563'
};

/** Freitext-Farbnamen von vor der festen Auswahl, die keiner festen Farbe entsprechen. */
export const LEGACY_COLOR_SWATCHES: Record<string, string> = {
  silber: '#d1d5db',
  gold: '#d4af37'
};

/** Freitext-Synonyme, die nicht woertlich einer Uebersetzung entsprechen, aber denselben Farbschluessel meinen. */
const EXTRA_SYNONYMS: Record<string, CollarColorKey> = {
  lila: 'violet',
  gruen: 'green',
  tuerkis: 'turquoise',
  hellgruen: 'lightGreen',
  dunkelgruen: 'darkGreen',
  helltuerkis: 'lightTurquoise',
  dunkeltuerkis: 'darkTurquoise',
  weiß: 'white'
};

let reverseMap: Record<string, CollarColorKey> | null = null;

/** Baut einmalig eine Umkehrtabelle aus allen Sprachversionen von breeding:plan.collarColors, damit auch mit einer anderen Sprache erfasste Alteintraege wiedererkannt werden. */
const getReverseMap = (): Record<string, CollarColorKey> => {
  if (reverseMap) return reverseMap;

  const map: Record<string, CollarColorKey> = { ...EXTRA_SYNONYMS };
  for (const lng of SUPPORTED_LANGUAGES) {
    const bundle = i18n.getResourceBundle(lng, 'breeding')?.plan?.collarColors as Record<string, string> | undefined;
    if (!bundle) continue;
    for (const key of COLLAR_COLOR_KEYS) {
      const translated = bundle[key];
      if (translated) map[translated.trim().toLowerCase()] = key;
    }
  }
  reverseMap = map;
  return map;
};

/** Ordnet einen frei eingegebenen oder frueher gespeicherten Farbnamen (in beliebiger App-Sprache) dem festen Farbschluessel zu, falls moeglich. */
export const collarColorKeyFor = (color: string): CollarColorKey | null => {
  const normalized = color.trim().toLowerCase();
  // Schluessel wie "lightBlue" sind camelCase - verglichen wird ohne Gross-/Kleinschreibung
  const key = COLLAR_COLOR_KEYS.find((k) => k.toLowerCase() === normalized);
  if (key) return key;
  return getReverseMap()[normalized] ?? null;
};
